import propTypes from 'prop-types';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import Styled from './form-wrapper-style';
import Loader from '../loader/loader-component';

/**
 * Ettx React Hook Form Wrapper component with default values
 *
 * @param {object} props - The props object
 * @param {JSX.Element} props.children - The children Form components
 * @param {object} props.defaultValues - The default values for the form fields
 * @param {Function} props.onSubmit - The onSubmit function for the react-hook-forms
 * @param {boolean} props.onEnterKeyPress - if enter key is pressed.
 * @returns {JSX.Element}
 */
const FormWrapper = ({
  children,
  defaultValues,
  onSubmit,
  onEnterKeyPress,
}) => {
  const methods = useForm({
    shouldFocusError: true,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  return (
    <Styled.MainContainer>
      <FormProvider {...methods}>
        <FormContent onEnterKeyPress={onEnterKeyPress} onSubmit={onSubmit}>
          {children}
        </FormContent>
      </FormProvider>
    </Styled.MainContainer>
  );
};

/**
 * Form content component
 *
 * @param {object} props - The props object
 * @param {JSX.Element} props.children - Insert your form component here
 * @param {Function} props.onSubmit - The onSubmit function
 * @param {boolean} props.onEnterKeyPress - if enter key is pressed.
 * @returns {JSX.Element}
 */
const FormContent = ({ children, onSubmit, onEnterKeyPress }) => {
  const {
    formState: { isLoading },
    handleSubmit,
  } = useFormContext();

  if (isLoading) {
    return <Loader loading />;
  }
  const checkKeyDown = (e) => {
    if (e.key === 'Enter' && !onEnterKeyPress) {
      e.preventDefault();
    }
  };

  return (
    <Styled.Form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => checkKeyDown(e)}
    >
      {children}
    </Styled.Form>
  );
};

FormContent.propTypes = {
  /** The children Form components */
  children: propTypes.node.isRequired,
  /** The onSubmit function */
  onSubmit: propTypes.func.isRequired,
  /* If the enter key has been pressed */
  onEnterKeyPress: propTypes.func,
};

FormContent.defaultProps = {
  onEnterKeyPress: undefined,
};

FormWrapper.propTypes = {
  /** The onSubmit function */
  onSubmit: propTypes.func.isRequired,
  /** The children Form components */
  children: propTypes.node.isRequired,
  /** The default values for the form */
  defaultValues: propTypes.shape({}),
  /* If the enter key has been pressed */
  onEnterKeyPress: propTypes.func,
};

FormWrapper.defaultProps = {
  defaultValues: {},
  onEnterKeyPress: undefined,
};

export default FormWrapper;
